import { useMemo } from "react"
import { map } from "lodash"
import { useOutletContext } from "react-router-dom"
import { useRouterParam } from "hooks/router"
import { numberFormatter } from "helpers/formatters/numbers"
import { useContentSetMetadata, useContentSetMetrics } from "resources/content-sets"
import { ERROR, LOADING, OK, combineStatuses } from "helpers/data-statuses"
import { COLORS } from "helpers/analyzeConstants"
import Loading from "components/loading"
import Error from "components/error"
import { createContentSetExposureSingleDateQuery } from "gatherers/single-date/content-set-exposure"
import { createContentSetExposureMultiDateQuery } from "gatherers/multi-date/content-set-exposure"
import CompareGraph from "../renderers/compare-graph"
import { useSelectedMetric } from "../hooks/selected-metric"
import DataIntegratedSection from "./data-integrated-section"

export default function CompareContentSet() {
  const context = useOutletContext()
  const { modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric } = context
  const contentSetId = useRouterParam("contentSetId")
  const [contentSetMetadata, contentSetStatus] = useContentSetMetadata(contentSetId)

  const [availableMetrics, contentSetMetricsStatus] = useContentSetMetrics(contentSetId)
  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)

  const currentDate = selectedDate || endDate

  const status = useMemo(() => combineStatuses([contentSetStatus, contentSetMetricsStatus]), [contentSetStatus, contentSetMetricsStatus])

  const graph = useMemo(() => ({
    id: "graph",
    name: "Graph",
    renderer: CompareGraph,
    endDate,
    metrics: map(positionSets, set => createContentSetExposureMultiDateQuery(modelId, contentSetId, set.queryPositionSet, set.queryBase, startDate, endDate)),
    availableMetrics,
    selectedMetric,
    positionSetsMetadata: positionSets,
    tickFormatter: numberFormatter,
    separator: true,
  }), [positionSets, availableMetrics, selectedMetric, modelId, contentSetId, startDate, endDate])

  const sections = useMemo(() => {
    if (status !== OK) {
      return null
    }

    let firstExpandable = true
    return [
      graph,
      ...map(contentSetMetadata.categories, (category, index) => {
        let defaultExpanded
        if (firstExpandable && !category.singleFactor) {
          defaultExpanded = true
          firstExpandable = false
        }

        return {
          id: category.id,
          name: category.name,
          color: COLORS[index % COLORS.length],
          defaultExpanded,
          isGroup: true,
          metrics: map(positionSets, set => createContentSetExposureSingleDateQuery(modelId, contentSetId, set.queryPositionSet, set.queryBase, currentDate, { category: category.id })),
        }
      }),
    ]
  },
  // [TODO] how to improve reference integrity of position sets
  [status, graph, contentSetMetadata?.categories, positionSets, modelId, contentSetId, currentDate])

  if (status === ERROR) {
    return <Error />
  }

  if (status === LOADING) {
    return <Loading />
  }

  return sections.map((section, index) => {
    const configKey = `contentset-${contentSetId}-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={numberFormatter}
      />
    )
  })
}
